/**
 * The trip's map as a picture: our own tile server's render of the region
 * (tiles.trip-planner.com.au, the same style as the app), with each day's
 * road drawn on top in its colour and the planner's pins. It is in the
 * server HTML, so the map is never a grey box on first paint; the live
 * Google map fades in over it once its tiles are drawn.
 *
 * One coordinate space: the SVG's viewBox is the picture's size and
 * sharedTrip.staticView projects lat/lng into it, so the lines and pins sit
 * exactly on the picture however the box scales it ("slice": fill, centred).
 */
import React from 'react';
import { stopPinSvg, type StopRole } from '../../../components/planner/roles';
import { staticMapUrl, svgPath, type StaticView } from './sharedTrip';

type LatLng = { lat: number; lng: number };

export interface StaticLine {
  key: string;
  color: string;
  path: LatLng[];
  routed: boolean;
}

export interface StaticPin {
  key: string;
  position: LatLng;
  letter: string;
  role: StopRole;
  loop: boolean;
}


export default function StaticRouteMap({
  view,
  lines,
  pins,
  className = '',
  media,
}: {
  view: StaticView;
  lines: StaticLine[];
  pins: StaticPin[];
  className?: string;
  /** Only fetch the picture when this media query matches (the desktop column). */
  media?: string;
}) {
  const { width: w, height: h } = view;
  const url = staticMapUrl(view);
  return (
    <div className={`relative h-full w-full overflow-hidden bg-[#eef2f6] ${className}`} data-testid="trip-static-map">
      {/* The picture and the SVG crop the same way (cover / slice, centred), so they stay registered. */}
      <picture>
        {media && <source media={media} srcSet={url} />}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={media ? 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==' : url}
          alt=""
          aria-hidden="true"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </picture>
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 block h-full w-full"
      role="img"
      aria-label="Map of the route"
    >
      {lines.map((l) => {
        const d = svgPath(l.path.map(view.project));
        if (!d) return null;
        return (
          <g key={l.key} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d={d} stroke="#ffffff" strokeOpacity={0.95} strokeWidth={7} />
            <path d={d} stroke={l.color} strokeWidth={4} strokeDasharray={l.routed ? undefined : '1 7'} />
          </g>
        );
      })}
      {pins.map((p) => {
        const { x, y } = view.project(p.position);
        return (
          <g key={p.key} transform={`translate(${(x - 15).toFixed(1)} ${(y - 40).toFixed(1)})`}>
            <g dangerouslySetInnerHTML={{ __html: stopPinSvg(p.letter, p.role) }} />
          </g>
        );
      })}
    </svg>
    </div>
  );
}
